/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed, get, set } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import type { BarData } from '@mylighthouse/prism-ember/types/components/prism/data-bar';
import { task } from 'ember-concurrency';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import {
  COLOR_BOOKING_LY,
  COLOR_TEXT_CRITICAL,
  COLOR_TEXT_DEFAULT,
  COLOR_TEXT_SUCCESS,
  COLOR_TRAFFIC_LIGHT_NEUTRAL,
  getCssVariableValue,
} from 'frontend-utils/utils/css-variable';
import type CalculatedValues from 'lumen/utils/datamodels/revenue-insight/calculated-values';
import {
  getFormattedChange,
  getNegativeReferenceValuePercentage,
  getPositiveReferenceValuePercentage,
} from 'lumen/utils/revenue-insight/ri-utils';
import {
  changeValueFormat,
  valueFormat,
} from 'lumen/utils/revenue-insight/value-formatting';
import { timeout } from 'lumen/utils/waiters';

export interface SegmentationItem {
  calculatedValues: CalculatedValues;
  children: SegmentationItem[];
  displayValue: string;
  hasNoData: boolean;
  id: string;
  isAggregatedRow: boolean;
  isDisabled: boolean;
  isExpanded: boolean;
  isSelectable: boolean;
  meta?: {
    issues?: string[];
  };
  parent: SegmentationItem | null;
  remainingValues: string[];
  segmentationId: string;
  segmentationValue: string;
  segmentationValueId: string;
}

export interface HotelAndMarketRevenueInsightBaseSegmentationBlockItemBaseSignature {
  Args: {
    item: SegmentationItem;
    maxValues?: {
      BOB: number;
      change: number;
    };
    params: {
      isDisabled: boolean;
      kpi: string;
      segmentationFilterClicked: (
        segmentationId: string,
        segmentationValueId: string,
      ) => void;
    };
  };
}

export default class HotelAndMarketRevenueInsightBaseSegmentationBlockItemBase<
  T extends HotelAndMarketRevenueInsightBaseSegmentationBlockItemBaseSignature,
> extends Component<T> {
  @service declare intl: Services['intl'];

  @service declare riSelections: Services['ri-selections'];

  isHovered = false;

  // These are tasks because they sometimes fired when a rerender was happening, causing errors
  onMouseEnterTask = task({ restartable: true }, async (): Promise<void> => {
    await timeout();
    set(this, 'isHovered', true);
  });

  onMouseLeaveTask = task({ restartable: true }, async (): Promise<void> => {
    await timeout();
    set(this, 'isHovered', false);
  });

  @computed(
    'args.item.calculatedValues.{budgetChange,change,forecastChange}',
    'isPickup',
    'metricKey',
    'riSelections.{changeKey,isBudgetActive,isForecastActive}',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get changeValue() {
    let changeValue = null;

    if (this.riSelections?.isBudgetActive && !this.isPickup) {
      changeValue =
        this.args.item?.calculatedValues?.budgetChange?.[this.metricKey];
    } else if (this.riSelections?.isForecastActive && !this.isPickup) {
      changeValue =
        this.args.item?.calculatedValues?.forecastChange?.[this.metricKey];
    } else {
      changeValue =
        this.args.item?.calculatedValues?.[this.riSelections.changeKey]?.[
          this.metricKey
        ];
    }

    // Round [-0.5, 0.499...] to 0. This prevent rounding errors where e.g. -0.42 is displayed as +0 in red (since it's negative).
    // With this rounding it will display the change as a neutral change value of 0.
    if (
      changeValue !== null &&
      changeValue !== undefined &&
      Math.round(changeValue) === 0
    ) {
      return 0;
    }

    return changeValue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChange() {
    return getFormattedChange(this.changeValue, this.intl);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChangeValueWithPercentage() {
    return changeValueFormat(
      this.changeValue,
      Boolean(this.showPercentages),
      this.intl,
    );
  }

  @computed('args.maxValues.change')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedMaxChange() {
    return this.args.maxValues?.change || 1;
  }

  @computed('args.maxValues.BOB')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedMaxValue() {
    return this.args.maxValues?.BOB || 1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedPickupLastYear() {
    return this.pickupLastYear || 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValue() {
    return this.value || 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValueWithPercentage() {
    return valueFormat(this.value, Boolean(this.showPercentages), this.intl);
  }

  @computed('isTargetActive', 'args.item.segmentationId')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasTarget() {
    return this.isTargetActive && this.args.item?.segmentationId !== 'pickup';
  }

  // Loops over parents and determines indendation amount
  @computed('args.item.parent')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get indentationAmount() {
    let nestingLevel = 1; // Start at 1 because this is the number of div.indent-container's that we show, and we always show at least 1.
    let { parent } = this.args.item;
    while (parent) {
      nestingLevel += 1;
      parent = parent.parent;
    }
    return nestingLevel;
  }

  @computed(
    'args.item.isDisabled',
    'args.params.{filterLimits,isDisabled,segmentationName}',
    'isSelected',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDisabled() {
    // If an item is selected, you can always unselect it
    if (this.isSelected) {
      return false;
    }

    return this.args.item.isDisabled || this.args.params.isDisabled;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPickup() {
    return false;
  }

  // Checks filters if this is selected or if any parent is selected
  @computed(
    'args.item.{parent,segmentationId,segmentationValueId}',
    'riSelections.parsedSegmentationFilters',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSelected() {
    const currentFilters =
      this.riSelections.parsedSegmentationFilters[
        this.args.item.segmentationId
      ];

    if (!currentFilters) {
      return false;
    }

    if (currentFilters.indexOf(this.args.item.segmentationValueId) !== -1) {
      return true;
    }

    let { parent } = this.args.item;
    let isParentselected = false;
    while (parent && !isParentselected) {
      isParentselected =
        currentFilters.indexOf(parent.segmentationValueId) !== -1;
      parent = parent.parent;
    }

    return isParentselected;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isTargetActive() {
    return this.riSelections?.isTargetActive;
  }

  @computed('args.params.kpi')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpi() {
    return this.args.params?.kpi;
  }

  // Shared with kpi-list-item.js.
  // For everything other than KpiListItems, we use the short keys  e.g. nrOfRooms, revenue.
  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get metricKey() {
    return this.kpi;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get negativeReferenceValuePercentage() {
    return getNegativeReferenceValuePercentage(
      this.changeValue,
      this.formattedMaxChange,
    );
  }

  @computed('args.item.calculatedValues.pickupLastYear', 'metricKey')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pickupLastYear() {
    return this.args.item?.calculatedValues?.pickupLastYear?.[this.metricKey];
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pickupLastYearPercentage() {
    const value = (this.formattedPickupLastYear / this.formattedMaxValue) * 100;
    return Math.max(0, value);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get positiveReferenceValuePercentage() {
    return getPositiveReferenceValuePercentage(
      this.changeValue,
      this.formattedMaxChange,
    );
  }

  @computed(
    'args.item.calculatedValues.{reference,cancellationReference}',
    'metricKey',
    'riSelections.referenceKey',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referenceValue() {
    const { referenceKey } = this.riSelections;

    // Value can be missing because of it being a remaining element in RI MP.
    return this.args.item?.calculatedValues?.[referenceKey]?.[this.metricKey];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showPercentages() {
    return false;
  }

  // We don't want tooltips for segment values with 0 - 0 values or N/A - N/A
  @computed(
    'args.item.{isDisabled,segmentationId}',
    'metricKey',
    'referenceValue',
    'riSelections.isTargetActive',
    'value',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showTooltip() {
    if (
      this.args.item.segmentationId === RI_BREAKDOWNS.PICKUP &&
      this.args.item.isDisabled
    ) {
      return false;
    }

    const hasValue = Number.isFinite(this.value);
    const hasReferenceValue = Number.isFinite(this.referenceValue);

    let hasTargetValue = false;
    if (this.riSelections.isTargetActive) {
      // If budget/forecast is active and it has a value, show the tooltip
      const hasBudget = Number.isFinite(
        get(this.args, `item.calculatedValues.budget.${this.metricKey}`),
      );
      const hasForecast = Number.isFinite(
        get(this.args, `item.calculatedValues.forecast.${this.metricKey}`),
      );
      hasTargetValue = hasBudget || hasForecast;
    }

    return hasValue || hasReferenceValue || hasTargetValue;
  }

  @computed(
    'args.item.calculatedValues.{budget,forecast}',
    'metricKey',
    'riSelections.isBudgetActive',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get targetValue() {
    if (this.riSelections.isBudgetActive) {
      return this.args.item?.calculatedValues?.budget?.[this.metricKey] ?? null;
    }
    return this.args.item?.calculatedValues?.forecast?.[this.metricKey] ?? null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get targetValuePercentage() {
    const value = ((this.targetValue ?? 0) / this.formattedMaxValue) * 100;
    return Math.max(0, value);
  }

  @computed(
    'args.item.calculatedValues.{BOB,cancellationBOB}',
    'riSelections.bobKey',
    'metricKey',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get value() {
    const { bobKey } = this.riSelections;

    // Value can be missing because of it being a remaining element in RI MP.
    return this.args.item?.calculatedValues?.[bobKey]?.[this.metricKey];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get valueColor() {
    if (!this.changeValue) {
      return htmlSafe('');
    }

    let color = getCssVariableValue(COLOR_TEXT_DEFAULT);
    if (this.changeValue < 0) {
      color = this.riSelections.isCancellationsOnlyView
        ? getCssVariableValue(COLOR_TEXT_SUCCESS)
        : getCssVariableValue(COLOR_TEXT_CRITICAL);
    }
    if (this.changeValue > 0) {
      color = this.riSelections.isCancellationsOnlyView
        ? getCssVariableValue(COLOR_TEXT_CRITICAL)
        : getCssVariableValue(COLOR_TEXT_SUCCESS);
    }

    return htmlSafe(`color: ${color};`);
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get valuePercentage() {
    const value = (this.formattedValue / this.formattedMaxValue) * 100;
    return Math.max(0, value);
  }

  @computed(
    'args.maxValues.BOB',
    'isTargetActive',
    'pickupLastYearPercentage',
    'riSelections.isAdrSelected',
    'value',
    'valuePercentage',
  )
  get valueProgressBarData(): BarData[] {
    const valueProgressBarData = [
      {
        widthPercentage: this.valuePercentage,
        backgroundColor: getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEUTRAL),
      },
    ] as BarData[];

    // Only show Pickup Last Year when comparing to a target (Budget or Forecast) and never show Pickup Last Year when ADR total is selected
    if (this.isTargetActive && !this.riSelections.isAdrSelected) {
      valueProgressBarData.push({
        widthPercentage: this.pickupLastYearPercentage,
        backgroundColor: getCssVariableValue(COLOR_BOOKING_LY),
      });
    }

    return valueProgressBarData;
  }

  @action selectSegmentationItem(segmentationItem: SegmentationItem): void {
    if (!this.isDisabled && segmentationItem.isSelectable) {
      this.args.params?.segmentationFilterClicked(
        segmentationItem.segmentationId,
        segmentationItem.segmentationValueId,
      );
    }
  }
}
